import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  StaticProvider
} from '@angular/core';

import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';
import { LazyComponentsService } from 'src/app/core/services/lazy-components/lazy-components.service';
import { parseChange } from 'src/app/utils/functions/change-helpers';
import { noop } from 'src/app/utils/functions/noop';

/**
 * This component retrieves a module from `LazyComponentsService` by given module id,
 * creates it's root component and injects it into the template container.
 *
 * Optional providers will be added to the module's injector.
 */
@Component({
  selector: 'cc-lazy-host',
  templateUrl: './lazy-host.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule]
})
export class LazyHostComponent implements OnChanges, OnDestroy {
  @Input() public componentId!: LazyComponentId;
  @Input() public providers!: StaticProvider[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public lazyComponent: any = undefined;
  public customInjector: Injector | undefined = undefined;

  public constructor(
    private readonly lazyComponentsService: LazyComponentsService,
    private readonly injector: Injector,
    private readonly changeDetector: ChangeDetectorRef
  ) {}

  public ngOnChanges({ componentId }: SimpleChanges): void {
    const change = parseChange(componentId);
    if (change.changed) {
      if (!change.toTruthy) {
        this.detachComponent();
      }
      this.loadComponent().catch(noop);
    }
  }

  public ngOnDestroy(): void {
    this.detachComponent();
  }

  private async loadComponent(): Promise<void> {
    if (!this.componentId) {
      return;
    }

    this.customInjector = Injector.create({
      providers: [...(this.providers || [])],
      parent: this.injector
    });

    this.lazyComponent = await this.lazyComponentsService.loadComponent(this.componentId);
    this.changeDetector.markForCheck();
  }

  private detachComponent(): void {
    this.lazyComponent = undefined;
    this.changeDetector.markForCheck();
  }
}
